import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useContentCalendar } from './use-content'
import { PostCard } from './post-card'
import type { Post, PostStatus } from './types'
import { STATUS_LABELS } from './types'

const STATUS_DOT_COLOR: Record<PostStatus, string> = {
  draft: 'bg-gray-400',
  pending_review: 'bg-yellow-400',
  approved: 'bg-green-400',
  published: 'bg-blue-500',
  scheduled: 'bg-purple-500',
  failed: 'bg-red-500',
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const MAX_VISIBLE_DOTS = 3

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

function toISODate(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

type CalendarCellProps = {
  readonly day: number
  readonly isToday: boolean
  readonly isCurrentMonth: boolean
  readonly posts: readonly Post[]
  readonly onClick: (posts: readonly Post[], date: string) => void
  readonly dateStr: string
}

function CalendarCell({ day, isToday, isCurrentMonth, posts, onClick, dateStr }: CalendarCellProps) {
  const visiblePosts = posts.slice(0, MAX_VISIBLE_DOTS)
  const overflow = posts.length - MAX_VISIBLE_DOTS

  return (
    <div
      className={cn(
        'min-h-[80px] rounded-md border p-1.5 transition-colors',
        isCurrentMonth ? 'bg-card' : 'bg-muted/30',
        posts.length > 0 && 'cursor-pointer hover:border-cobalt-bright',
      )}
      onClick={() => posts.length > 0 && onClick(posts, dateStr)}
      role={posts.length > 0 ? 'button' : undefined}
      tabIndex={posts.length > 0 ? 0 : undefined}
      onKeyDown={(e) => {
        if (posts.length > 0 && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick(posts, dateStr)
        }
      }}
      aria-label={posts.length > 0 ? `${posts.length} post(s) on ${dateStr}` : undefined}
    >
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
          isToday && 'bg-primary text-primary-foreground',
          !isCurrentMonth && 'text-muted-foreground',
          isCurrentMonth && !isToday && 'text-foreground',
        )}
      >
        {day}
      </span>

      {/* Post dots */}
      <div className="mt-1 space-y-0.5">
        {visiblePosts.map((post) => (
          <div key={post.id} className="flex items-center gap-1">
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT_COLOR[post.status])} />
            <span className="truncate text-[10px] text-muted-foreground leading-none">
              {post.topic ?? 'Post'}
            </span>
          </div>
        ))}
        {overflow > 0 && (
          <p className="text-[10px] text-muted-foreground">+{overflow} more</p>
        )}
      </div>
    </div>
  )
}

export function ContentCalendar() {
  const today = new Date()
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())
  const [selectedPosts, setSelectedPosts] = useState<readonly Post[] | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const { data: calendarEntries = [] } = useContentCalendar()

  // Build a lookup map: ISO date string → posts
  const postsByDate = new Map<string, Post[]>()
  for (const entry of calendarEntries) {
    postsByDate.set(entry.date, [...entry.posts])
  }

  function prevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear((y) => y - 1)
    } else {
      setCurrentMonth((m) => m - 1)
    }
  }

  function nextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear((y) => y + 1)
    } else {
      setCurrentMonth((m) => m + 1)
    }
  }

  function goToToday() {
    setCurrentYear(today.getFullYear())
    setCurrentMonth(today.getMonth())
  }

  function handleCellClick(posts: readonly Post[], date: string) {
    setSelectedPosts(posts)
    setSelectedDate(date)
  }

  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth)

  const monthLabel = new Date(currentYear, currentMonth, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  // Build grid cells (blanks for days before the 1st)
  const cells: Array<{ day: number; dateStr: string } | null> = [
    ...Array<null>(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      dateStr: toISODate(currentYear, currentMonth, i + 1),
    })),
  ]

  // Pad to complete last row
  const remainder = cells.length % 7
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) {
      cells.push(null)
    }
  }

  const todayStr = toISODate(today.getFullYear(), today.getMonth(), today.getDate())

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-muted/30 px-4 py-2">
        <span className="text-xs font-medium text-muted-foreground">Status:</span>
        {(Object.entries(STATUS_DOT_COLOR) as [PostStatus, string][]).map(([status, dotClass]) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className={cn('h-2 w-2 rounded-full', dotClass)} />
            <span className="text-xs text-muted-foreground">{STATUS_LABELS[status]}</span>
          </div>
        ))}
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{monthLabel}</h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={prevMonth} className="h-8 w-8 p-0">
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous month</span>
          </Button>
          <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 w-8 p-0">
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next month</span>
          </Button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="py-1 text-center text-xs font-medium text-muted-foreground">
            {wd}
          </div>
        ))}

        {/* Day cells */}
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`blank-${idx}`} className="min-h-[80px]" />
          }
          const posts = postsByDate.get(cell.dateStr) ?? []
          return (
            <CalendarCell
              key={cell.dateStr}
              day={cell.day}
              dateStr={cell.dateStr}
              isToday={cell.dateStr === todayStr}
              isCurrentMonth
              posts={posts}
              onClick={handleCellClick}
            />
          )
        })}
      </div>

      {/* Slide-over for selected day's posts */}
      <Sheet open={selectedPosts !== null} onOpenChange={(open) => !open && setSelectedPosts(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader className="pb-4">
            <SheetTitle>
              Posts on{' '}
              {selectedDate &&
                new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
            </SheetTitle>
            <SheetDescription>
              {selectedPosts?.length ?? 0} post(s) scheduled for this day.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4">
            {selectedPosts?.map((post) => (
              <PostCard
                key={post.id}
                post={post}
              />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
